import { useEffect, useMemo, useState, useCallback } from "react";
import images from "../../../assets/images";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import axiosInstance from "../../../common/axiosInstance";
import type { ColDef } from "ag-grid-community";
import {
  usePremisesStore,
  useRegionStore,
  useOrgUsersStore,
} from "../../../store/store";
import { toast } from "react-toastify";
import { QrCode, Download } from "lucide-react";
import { format } from "date-fns";
import { OrgUserType, WashroomTypeListType } from "../../../types/api";
import { WASHROOM_MANAGEMENT } from "../../../constants/modules";
import { usePermissions } from "../../../hooks/usePermission";
import { useAuth } from "../../../auth/AuthProvider";
import { PageLoader, SpinLoader } from "../../../components/loader/Loader";
import QRCodeModal from "../../../components/QRCodeModal/QRCodeModal";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import { getAllTypeList } from "../../../service/api";

interface Washroom {
  _id: string;
  premisesId: string;
  regionId: string;
  washroomName: string;
  type: string;
  floor: string;
  assignedStaff: string;
  createdDate: string;
  tenantId: string;
  createdAt: string;
  currentStatus: string;
  lastCleanedAt?: string;
  needsCleaning?: boolean;
}

export function showConfirmToast(message: string, onConfirm: () => void) {
  const ToastContent = () => (
    <div>
      <div>{message}</div>
      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        <button
          onClick={() => {
            toast.dismiss();
            onConfirm();
          }}
          style={{
            color: "white",
            background: "#0fa9a6",
            border: "none",
            padding: "4px 12px",
            borderRadius: 4,
          }}
        >
          Yes
        </button>
        <button
          onClick={() => toast.dismiss()}
          style={{
            color: "#333",
            background: "#eee",
            border: "none",
            padding: "4px 12px",
            borderRadius: 4,
          }}
        >
          No
        </button>
      </div>
    </div>
  );
  toast(<ToastContent />, { autoClose: false });
}

const WashroomMaintenance = () => {
  const navigate = useNavigate();
  const [washrooms, setWashrooms] = useState<Washroom[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPremises, setLoadingPremises] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingOrgUsers, setLoadingOrgUsers] = useState(false);
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [rowData, setRowData] = useState<Washroom[]>();
  const [error, setError] = useState("");
  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const [summaryStats, setSummaryStats] = useState({
    pending: 0,
    inProgress: 0,
    total: 0,
    needsCleaning: 0,
  });
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // QR Code states
  const [qrCodeData, setQrCodeData] = useState<any>(null);
  const [showQRModal, setShowQRModal] = useState<boolean>(false);

  const { canRead, canUpdate, canCreate } = usePermissions();
  const { roleData, user } = useAuth(); // include user for assigned filtering

  // Workflow tier comes from the role's `staffCategory`, not from CRUD
  // permission flags - those only gate raw API/UI access, not job function.
  const isOrgAdminAccount =
    user?.roles.includes("orgadmin") || user?.roles.includes("superadmin");
  const staffCategory = roleData?.staffCategory;
  const isAdminTier = isOrgAdminAccount || staffCategory === "admin";
  const isWashroomMaintenanceTier =
    !isAdminTier && staffCategory === "maintenance";
  const isAssignedOnlyTier = !isAdminTier && !isWashroomMaintenanceTier;

  const [washroomTypeList, setWashroomTypeList] = useState<
    WashroomTypeListType[]
  >([]);

  const fetchTypes = async () => {
    try {
      setLoading(false);
      const res = await getAllTypeList();

      if (res.data?.data && Array.isArray(res.data.data)) {
        setWashroomTypeList(res.data.data);
      } else {
        setWashroomTypeList([]);
      }
    } catch (error) {
      console.error("Failed to fetch washroom types: ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchWashrooms = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axiosInstance.get(`/api/washroom`);
      const washroomData = response.data.message || ([] as Washroom[]);

      // If user is not a washroom-performer (i.e., non-admin/general users),
      // limit visible washrooms to those assigned to the current user.
      const isAssignedOnly = isAssignedOnlyTier;
      const userId = user?.id || "";
      const userFullName = `${
        (user as any)?.firstName ||
        (user as any)?.first_name ||
        (user as any)?.name ||
        ""
      } ${(user as any)?.lastName || (user as any)?.last_name || ""}`.trim();

      const visibleWashrooms = isAssignedOnly
        ? washroomData.filter((w: Washroom) => {
            const assigned = w.assignedStaff;
            return (
              assigned === userId ||
              (typeof assigned === "string" && assigned === userFullName)
            );
          })
        : washroomData;

      setWashrooms(visibleWashrooms);

      const pending = visibleWashrooms.filter(
        (w: any) => w.currentStatus?.toLowerCase() === "needs attention",
      ).length;
      const inProgress = visibleWashrooms.filter(
        (w: any) => w.currentStatus?.toLowerCase() === "in-progress",
      ).length;

      setSummaryStats((prev) => ({
        ...prev,
        pending,
        inProgress,
        total: visibleWashrooms.length,
      }));
    } catch (err) {
      setError("Failed to fetch washroom data");
      console.error("Fetch washrooms error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPremisesData = async () => {
    setLoadingPremises(true);
    try {
      await fetchPremises();
    } catch (err) {
      console.error("Failed to fetch premises:", err);
      toast.error("Failed to load premises data");
    } finally {
      setLoadingPremises(false);
    }
  };

  const fetchRegionsData = async () => {
    setLoadingRegions(true);
    try {
      await fetchRegions();
    } catch (err) {
      console.error("Failed to fetch regions:", err);
      toast.error("Failed to load regions data");
    } finally {
      setLoadingRegions(false);
    }
  };

  const fetchOrgUsersData = async () => {
    setLoadingOrgUsers(true);
    try {
      await fetchOrgUsers();
    } catch (err) {
      console.error("Failed to fetch org users:", err);
      toast.error("Failed to load staff data");
    } finally {
      setLoadingOrgUsers(false);
    }
  };

  useEffect(() => {
    if (!selectedPremises || selectedPremises === "All Premises") {
      setRowData(washrooms);
    } else {
      const filteredData = Array.isArray(washrooms)
        ? washrooms.filter((drill) => drill.premisesId === selectedPremises)
        : [];
      setRowData(filteredData);
    }
  }, [washrooms, selectedPremises]);

  const handlePremiseChange = (value: string) => {
    setSelectedPremises(value);
  };

  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    setSelectedPremises("");
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          fetchPremisesData(),
          fetchRegionsData(),
          fetchOrgUsersData(),
        ]);
        await fetchWashrooms();
        await checkCleaningStatus();
      } catch (error) {
        console.error("Failed to load initial data:", error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadData();

    const interval = setInterval(checkCleaningStatus, 2 * 60 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkCleaningStatus = async () => {
    try {
      const statusResponse = await axiosInstance.get(
        `/api/washroom/check-status`,
      );

      const backendFlagged = statusResponse.data?.washrooms || [];

      // Get fresh washroom data from the API to avoid stale state
      const washroomsResponse = await axiosInstance.get(`/api/washroom`);

      const freshWashrooms = washroomsResponse.data.message || [];

      // Apply assigned-only filter same as fetchWashrooms
      const isAssignedOnly = isAssignedOnlyTier;
      const userId = user?.id || "";
      const userFullName = `${
        (user as any)?.firstName ||
        (user as any)?.first_name ||
        (user as any)?.name ||
        ""
      } ${(user as any)?.lastName || (user as any)?.last_name || ""}`.trim();

      const visibleFreshWashrooms = isAssignedOnly
        ? freshWashrooms.filter((w: Washroom) => {
            const assigned = w.assignedStaff;
            return (
              assigned === userId ||
              (typeof assigned === "string" && assigned === userFullName)
            );
          })
        : freshWashrooms;

      const notCleanedWashrooms = visibleFreshWashrooms.filter(
        (w: Washroom) => {
          const normalizedStatus = w.currentStatus?.toLowerCase() || "";
          return (
            normalizedStatus === "not cleaned" ||
            normalizedStatus === "needs attention" ||
            !w.currentStatus
          );
        },
      );

      const backendFlaggedIds = (backendFlagged || []).map((w: any) => w._id);
      // Only consider backend-flagged IDs that are visible to the user
      const visibleBackendFlaggedIds = backendFlaggedIds.filter((id: string) =>
        visibleFreshWashrooms.some((w: Washroom) => w._id === id),
      );

      const combined = Array.from(
        new Set([
          ...visibleBackendFlaggedIds,
          ...notCleanedWashrooms.map((w: Washroom) => w._id),
        ]),
      );

      // Update both washrooms state and summary stats using visible set
      setWashrooms(
        visibleFreshWashrooms.map((w: Washroom) => ({
          ...w,
          needsCleaning: combined.includes(w._id),
        })),
      );

      setSummaryStats((prev) => ({
        ...prev,
        needsCleaning: combined.length,
        total: visibleFreshWashrooms.length,
        pending: visibleFreshWashrooms.filter(
          (w: any) => w.currentStatus?.toLowerCase() === "needs attention",
        ).length,
        inProgress: visibleFreshWashrooms.filter(
          (w: any) => w.currentStatus?.toLowerCase() === "in-progress",
        ).length,
      }));

      setLastChecked(new Date());
    } catch (error) {
      console.error("Failed to check cleaning status:", error);
      toast.error("Failed to check cleaning status");
    }
  };

  const isStatusButtonEnabled = (status: string) => {
    const normalizedStatus = status?.toLowerCase() || "";
    return (
      normalizedStatus === "in-progress" ||
      normalizedStatus === "not cleaned" ||
      normalizedStatus === ""
    );
  };

  // QR Code handler function
  const handleQRCode = useCallback(
    async (
      washroomId: string,
      action: "preview" | "download" = "preview",
      format: "png" | "svg" = "png",
    ) => {
      try {
        setLoading(true);

        if (action === "download") {
          // Download directly
          const response = await axiosInstance.get(
            `/api/washroom/qr/${washroomId}/download?format=${format}`,
            {
              responseType: "blob",
            },
          );

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `washroom-${washroomId}-qr.${format}`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success("QR Code downloaded successfully");
        } else {
          // Preview - get QR code data
          const response = await axiosInstance.get(
            `/api/washroom/qr/${washroomId}`,
          );

          // Display the QR code in modal
          const washroom = washrooms.find((w) => w._id === washroomId);
          setQrCodeData({
            ...response.data,
            washroomName: washroom?.washroomName || "Washroom",
            qrData: {
              ...response.data.qrData,
              washroomId: washroomId,
            },
          });
          setShowQRModal(true);
        }
      } catch (error) {
        console.error("Error with QR code:", error);
        toast.error(`Failed to ${action} QR code`);
      } finally {
        setLoading(false);
      }
    },
    [washrooms],
  );

  const actionCellRenderer = (params: any) => (
    <div className="flex gap-2 items-center justify-center">
      {canRead(WASHROOM_MANAGEMENT) && (
        <button
          className="focus:outline-none p-1 hover:bg-gray-100 rounded transition-colors"
          onClick={() =>
            navigate(
              `/washroom-management/washrooms/view-washroom?id=${params.data._id}`,
              {
                state: { washroom: params.data },
              },
            )
          }
          title="View"
        >
          <img
            className="w-5 h-5"
            src={images.viewIcon || "/placeholder.svg"}
            alt="View"
          />
        </button>
      )}

      {canUpdate(WASHROOM_MANAGEMENT) && (
        <button
          className="focus:outline-none p-1 hover:bg-gray-100 rounded transition-colors"
          onClick={() =>
            navigate(
              `/washroom-management/washrooms/update-washroom?id=${params.data._id}`,
              {
                state: { washroom: params.data },
              },
            )
          }
          title="Edit"
        >
          <img
            className="w-5 h-5"
            src={images.editIcon || "/placeholder.svg"}
            alt="Edit"
          />
        </button>
      )}

      {/* QR Code Actions */}
      <div className="flex gap-1">
        <button
          onClick={() => handleQRCode(params.data._id, "preview")}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          title="Preview QR Code"
        >
          <QrCode className="w-5 h-5 text-blue-600" />
        </button>
        {/* <button
          onClick={() => handleQRCode(params.data._id, "download", "png")}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          title="Download QR Code (PNG)"
        >
          <Download className="w-5 h-5 text-green-600" />
        </button> */}
      </div>
    </div>
  );

  const statusCellRenderer = (params: any) => {
    const status = params.value || "Unknown";
    const isOverdue = params.data.needsCleaning;
    const canUpdateStatus = isStatusButtonEnabled(params.value);
    const normalizedStatus = status.toLowerCase();
    const isAdmin = isAdminTier;
    const isWashroomMaintenance = isWashroomMaintenanceTier;
    const isNeedsAttention = normalizedStatus === "needs attention";

    if (isNeedsAttention) {
      if (isAdmin) {
        return (
          <div className="flex items-center gap-2">
            <button
              className={`px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 cursor-pointer hover:bg-red-200`}
              title="Needs Attention - Click to reassign staff"
              onClick={() => {
                navigate(
                  `/washroom-management/washrooms/update-status/${params.data._id}`,
                  {
                    state: {
                      washroom: params.data,
                      initialStatus: "Needs Attention",
                    },
                  },
                );
              }}
            >
              Needs Attention
            </button>
          </div>
        );
      } else if (isWashroomMaintenance) {
        return (
          <div className="flex items-center gap-2">
            <button
              className={`px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 cursor-pointer hover:bg-red-200`}
              title="Needs Attention - Click to reassign staff"
              onClick={() => {
                navigate(
                  `/washroom-management/washrooms/update-status/${params.data._id}`,
                  {
                    state: {
                      washroom: params.data,
                      initialStatus: "Needs Attention",
                    },
                  },
                );
              }}
            >
              Needs Attention
            </button>
          </div>
        );
      } else {
        // perform-washroom-task role sees "Attention Required"
        return (
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 cursor-default">
              Attention Required
            </span>
          </div>
        );
      }
    }

    if (
      isOverdue &&
      normalizedStatus !== "needs attention" &&
      normalizedStatus !== "in-progress"
    ) {
      return (
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 rounded-full text-sm font-medium cursor-pointer bg-red-100 text-red-800 hover:bg-red-200"
            title={
              isAdmin
                ? "Not Cleaned - Click to inspect details"
                : "Needs cleaning - overdue by 2 hours"
            }
            onClick={() => {
              navigate(
                `/washroom-management/washrooms/update-status/${params.data._id}`,
                {
                  state: {
                    washroom: params.data,
                    initialStatus: "Not Cleaned",
                  },
                },
              );
            }}
          >
            Not Cleaned
          </button>
        </div>
      );
    }

    const isNavigationAllowed =
      canUpdateStatus || normalizedStatus === "needs attention" || isAdmin;

    return (
      <div className="flex items-center gap-2">
        <button
          className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
            normalizedStatus === "clean"
              ? params.data.needsCleaning
                ? "bg-orange-100 text-orange-800"
                : "bg-green-100 text-green-800"
              : normalizedStatus === "in-progress"
                ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                : normalizedStatus === "closed"
                  ? "bg-gray-100 text-gray-800"
                  : normalizedStatus === "needs attention"
                    ? "bg-red-100 text-red-800 hover:bg-red-200"
                    : normalizedStatus === "not cleaned"
                      ? "bg-red-100 text-red-800 hover:bg-red-200"
                      : "bg-gray-100 text-gray-800"
          } ${isNavigationAllowed ? "cursor-pointer" : "cursor-default"}`}
          onClick={() => {
            if (isNavigationAllowed) {
              navigate(
                `/washroom-management/washrooms/update-status/${params.data._id}`,
                { state: { washroom: params.data } },
              );
            }
          }}
          title={
            isAdmin
              ? `${status} - Click to inspect details`
              : isOverdue
                ? "Needs cleaning - overdue by 2 hours"
                : canUpdateStatus || normalizedStatus === "needs attention"
                  ? "Click to update status"
                  : "Status cannot be updated"
          }
        >
          {status}
        </button>
      </div>
    );
  };

  const columnDefs: ColDef<Washroom>[] = useMemo(
    () =>
      [
        {
          headerName: "Region",
          field: "regionId",
          flex: 1,
          valueGetter: (params: any) => {
            const region = regions.find(
              (r) => r._id === params?.data?.regionId,
            );
            return region
              ? region.regionName
              : params?.data?.regionId || "Unknown";
          },
        },
        {
          headerName: "Premises",
          field: "premisesId",
          flex: 1,
          valueGetter: (params: any) => {
            const premise = allPremises.find(
              (p) => p.id === params?.data?.premisesId,
            );
            return premise
              ? premise.premisesName
              : params?.data?.premisesId || "Unknown";
          },
        },
        { headerName: "Washroom Name/ID", field: "washroomName", flex: 1 },
        { headerName: "Floor/Wing", field: "floor", flex: 1 },
        {
          headerName: "Washroom Type",
          field: "type",
          flex: 1,
          valueGetter: (params: any) => {
            const washroomType = washroomTypeList.find(
              (type) => type._id === params.data.type,
            );

            return washroomType && washroomType.typeName;
          },
        },
        {
          headerName: "Assigned Staff",
          field: "assignedStaff",
          flex: 1,
          valueGetter: (params: any) => {
            const staff = orgUsersData.find(
              (u: OrgUserType) => u._id === params?.data?.assignedStaff,
            );
            return staff
              ? `${staff.firstName} ${staff.lastName}`
              : params?.data?.assignedStaff || "Unknown";
          },
        },
        {
          headerName: "Status",
          field: "currentStatus",
          flex: 1,
          cellRenderer: statusCellRenderer,
        },
        {
          headerName: "Action",
          flex: 1.2,
          cellRenderer: actionCellRenderer,
        },
      ].filter(Boolean) as ColDef[],
    [
      regions,
      allPremises,
      orgUsersData,
      isAdminTier,
      isWashroomMaintenanceTier,
      handleQRCode,
    ],
  );

  const isLoading =
    loading || loadingPremises || loadingRegions || loadingOrgUsers;

  if (isInitialLoad) {
    return <PageLoader />;
  }

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-100vh">
        <div className="flex justify-between items-center">
          <h2 className="mb-3">Washroom Maintenance</h2>

          <div className="flex gap-4">
            <>
              <Dropdown
                selectedValue={selectedRegion}
                onChange={handleRegionChange}
                type="region"
              />
              <Dropdown
                selectedValue={selectedPremises}
                onChange={handlePremiseChange}
                type="premises"
                selectedRegion={selectedRegion}
              />
            </>

            <span className="flex items-center">
              {canCreate(WASHROOM_MANAGEMENT) && (
                <button
                  onClick={() =>
                    navigate("/washroom-management/washrooms/add-washroom")
                  }
                  className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
                >
                  <img
                    src={images.addPremiessIcon || "/placeholder.svg"}
                    width={25}
                    alt=""
                  />
                  ADD WASHROOM
                </button>
              )}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm font-medium">
              Total Washrooms
            </h3>
            <p className="text-2xl font-bold">{summaryStats.total}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm font-medium">Pending</h3>
            <p className="text-2xl font-bold text-red-500">
              {summaryStats.pending}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm font-medium">In Progress</h3>
            <p className="text-2xl font-bold text-yellow-500">
              {summaryStats.inProgress}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm font-medium">
              Due for Cleaning
            </h3>
            <p className="text-2xl font-bold text-orange-500">
              {summaryStats.needsCleaning}
            </p>
            {lastChecked && (
              <p className="text-xs text-gray-400">
                Last checked: {format(lastChecked, "HH:mm")}
              </p>
            )}
          </div>
        </div>

        <div
          className="ag-theme-alpine mt-2 bg-white rounded shadow"
          style={{ height: "60vh", width: "100%" }}
        >
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <SpinLoader />
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-full text-red-500">
              {error}
            </div>
          ) : (
            <AgGridReact
              rowData={rowData || []}
              columnDefs={columnDefs}
              rowHeight={50}
              headerHeight={65}
              suppressCellFocus={true}
              animateRows={true}
              domLayout="autoHeight"
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20]}
              paginationAutoPageSize={false}
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
            />
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        qrCodeData={qrCodeData}
        onDownloadPNG={() =>
          handleQRCode(qrCodeData?.qrData?.washroomId, "download", "png")
        }
        onDownloadSVG={() =>
          handleQRCode(qrCodeData?.qrData?.washroomId, "download", "svg")
        }
      />
    </>
  );
};

export default WashroomMaintenance;
